import { swSettings } from "app/common/services/swSettings";
import { EAvailabilityMode, isHidden, isLocked } from "app/common/services/pageClaims";
import type { INavItem } from "../../../types";
import { applyNavItemPermissions } from "app/pages/workspace/common/workspacesUtils";
import accountReviewStorage from "app/pages/sales-intelligence/sub-modules/account-overview/shared/services/storage/storage";
import {
    ACCOUNT_OVERVIEW_COMPANY,
    ACCOUNT_OVERVIEW_CONTACTS,
    WEBSITE_ANALYSIS_COMPETITORS,
    WEBSITE_ANALYSIS_TECHNOLOGIES,
} from "app/pages/sales-intelligence/constants/routes";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { getOverviewGroupInitiallyOpened } from "app/components/sidebar3/secondary-side-bar/secondary-bar-nav-obj/sales/utils";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { ECategoryAudienceType } from "app/pages/website-analysis/website-structure/pages/AudienceWrapper";

const navClaims = (getComponent: () => Parameters<typeof isLocked>[0]) => ({
    get lockIcon() {
        return isLocked(getComponent());
    },
    get hidden() {
        return isHidden(getComponent());
    },
});

const ignoreSearchKeywordsParams = {
    IncludeOrganic: null,
    IncludePaid: null,
    IncludeBranded: null,
    IncludeNoneBranded: null,
    IncludeNewKeywords: null,
    IncludeTrendingKeywords: null,
    IncludeQuestions: null,
    limits: null,
    BooleanSearchTerms: null,
};
const ignoreDisplayGroupParams = {
    sort: null,
    domain: null,
};

const companyGroup = {
    title: "si.account-overview.nav.company",
    name: "company",
    hideIfNoSubItems: true,
    itemComponentProps: {
        id: "account-review-company",
        text: (
            <I18n
                translatable={{ key: "si.account-overview.nav.company" }}
                data-automation="nav-bar-item-company"
            />
        ),
        defaultIsOpen: () => true,
        "data-automation-group": "nav-bar-item-company",
    },
    subItems: [
        {
            state: ACCOUNT_OVERVIEW_COMPANY,
            name: "company overview",
            title: "si.account-overview.company.title",
            itemComponentProps: {
                "data-automation-item": "account-overview.company-overview",
            },
        },
        {
            state: ACCOUNT_OVERVIEW_CONTACTS,
            name: "contacts",
            title: "si.account-overview.contacts.title",
            itemComponentProps: {
                "data-automation-item": "account-overview.contacts",
            },
        },
    ],
};

const getWebsiteGroup = () => ({
    title: "si.account-overview.nav.website",
    name: "website",
    hideIfNoSubItems: true,
    itemComponentProps: {
        id: "accountreview-website",
        itemText: (
            <I18n
                translatable={{ key: "si.account-overview.nav.website" }}
                data-automation="nav-bar-item-website"
            />
        ),
        "data-automation-group": "accountreview-website",
        defaultIsOpen: getOverviewGroupInitiallyOpened(),
    },
    subItems: [
        {
            title: "analysis.overview.performance.title",
            name: "worldwideOverview",
            state: "accountreview_website_overview_websiteperformance",
            itemComponentProps: {
                "data-automation-item": "analysis.overview.performance.title",
            },
            options: {
                isUSStatesSupported: true,
                isVirtualSupported: false,
            },
            ...navClaims(() => swSettings.components.WsWebOverview),
        },
        {
            title: "analysis.overview.technographics.title",
            name: "websitetechnologies",
            state: WEBSITE_ANALYSIS_TECHNOLOGIES,
            itemComponentProps: {
                "data-automation-item": "analysis.overview.technographics.title",
            },
            hidden: salesSettingsHelper.getWebsiteTechnologiesFeature() === EAvailabilityMode.HIDDEN,
            // lockIcon: isLocked(swSettings.components.WsWebTechnographics),
            // hidden: isHidden(swSettings.components.WsWebTechnographics),
        },
        {
            title: "analysis.overview.competitive.landscape.title",
            name: "competitiveLandscape",
            state: WEBSITE_ANALYSIS_COMPETITORS,
            itemComponentProps: {
                "data-automation-item": "analysis.overview.competitive.landscape.title",
            },
            options: {
                isVirtualSupported: false,
                isUSStatesSupported: true,
            },
            hidden: salesSettingsHelper.getWebsiteCompetitorsFeature() === EAvailabilityMode.HIDDEN,
            // lockIcon: isLocked(swSettings.components.WsWebCompetitors),
            // hidden: isHidden(swSettings.components.WsWebCompetitors),
        },
        {
            title: "analysis.traffic.engagement.title",
            name: "traffic-traffic&engagement",
            state: "accountreview_website_trafficandengagement",
            itemComponentProps: {
                "data-automation-item": "analysis.traffic.engagement.title",
            },
            options: {
                isUSStatesSupported: true,
                isVirtualSupported: false,
            },
            ...navClaims(() => swSettings.components.WsWebTrafficAndEngagement),
        },
        {
            title: "analysis.traffic.marketing.channels.title",
            name: "overview",
            itemComponentProps: {
                "data-automation-item": "analysis.traffic.marketing.channels.title",
            },
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            state: "accountreview_website_marketingchannels",
            ...navClaims(() => swSettings.components.WsWebTrafficChannels),
        },
        {
            title: "analysis.audience.geo.title",
            name: ECategoryAudienceType.Geography,
            itemComponentProps: {
                "data-automation-item": "analysis.audience.geo.title",
            },
            options: {
                isUSStatesSupported: false,
                isVirtualSupported: true,
            },
            state: "accountreview_website_audience_geography",
            ...navClaims(() => swSettings.components.WebGeography),
        },
        {
            title: "analysis.audience.demo.title",
            name: "demographics",
            itemComponentProps: {
                "data-automation-item": "analysis.audience.demo.title",
            },
            options: {
                isUSStatesSupported: false,
                isVirtualSupported: true,
            },
            state: "accountreview_website_audience_demographics",
            ...navClaims(() => swSettings.components.WebDemographics),
        },
        {
            title: "analysis.audience.interests.title",
            name: "audience",
            itemComponentProps: {
                "data-automation-item": "analysis.audience.interests.title",
            },
            options: {
                isVirtualSupported: false,
                isUSStatesSupported: true,
            },
            state: "accountreview_website_audience_interests",
            ...navClaims(() => swSettings.components.WebAudienceInterests),
        },
    ],
});

const searchGroup = {
    title: "Search",
    name: "search",
    hideIfNoSubItems: true,
    itemComponentProps: {
        id: "accountreview_search_group",
        text: "Search",
        isInitiallyOpened: () => accountReviewStorage.isWebsiteGroupOpened("accountreview_search_group"),
        "data-automation-group": "accountreview.search",
    },
    subItems: [
        {
            id: "accountreview_search_overview",
            title: "analysis.search.overview.nav.title",
            name: "searchOverview",
            state: "accountreview_website_search_overview",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            defaultQueryParams: {
                ...ignoreSearchKeywordsParams,
            },
            ...navClaims(() => swSettings.components.WsWebSearchOverview),
        },
        {
            id: "accountreview_search_keywords",
            title: "analysis.search.keywords.title",
            name: "searchKeywords",
            state: "accountreview_website_search_keyword",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            defaultQueryParams: {
                ...ignoreSearchKeywordsParams,
                IncludePaid: false,
                IncludeOrganic: true,
            },
            ...navClaims(() => swSettings.components.WsWebSearchKeywords),
        },
        {
            id: "accountreview_search_organic_competitors",
            title: "analysis.search.organic.competitors.title",
            state: "accountreview_website_search_organic_competitors",
            name: "competitorsOrganicKeywords",
            options: {
                isVirtualSupported: false,
                isUSStatesSupported: true,
            },
            ...navClaims(() => swSettings.components.WsWebOrganicSearchCompetitors),
            overrideParams: {
                duration: "3m",
                category: null,
            },
        },
        {
            id: "accountreview_search_ads",
            title: "analysis.search.ads.title",
            name: "searchAds",
            state: "accountreview_website_search_ads",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            defaultQueryParams: {
                ...ignoreSearchKeywordsParams,
            },
            ...navClaims(() => swSettings.components.WsWebSearchAds),
        },
    ],
};

const referralGroup = {
    title: "analysis.referrals.title",
    name: "websites",
    hideIfNoSubItems: true,
    itemComponentProps: {
        id: "accountreview_referral_group",
        text: "Referral",
        isInitiallyOpened: () => accountReviewStorage.isWebsiteGroupOpened("accountreview_referral_group"),
        "data-automation-group": "accountreview.search",
    },
    subItems: [
        {
            title: "analysis.referrals.incoming.title",
            name: "referrals",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            state: "accountreview_website_referrals_incomingtraffic",
            ...navClaims(() => swSettings.components.WebReferralIncomingTraffic),
            defaultQueryParams: {
                limits: null,
            },
        },
        {
            title: "analysis.referrals.outgoing.title",
            name: "outgoing",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            state: "accountreview_website_outgoingtraffic",
            ...navClaims(() => swSettings.components.WebReferralOutgoingTraffic),
        },
    ],
};

const displayGroup = {
    title: "Display",
    name: "display",
    hideIfNoSubItems: true,
    itemComponentProps: {
        id: "accountreview_display_group",
        text: "Display",
        isInitiallyOpened: () => accountReviewStorage.isWebsiteGroupOpened("accountreview_display_group"),
        "data-automation-group": "accountreview.display.title",
    },
    subItems: [
        {
            title: "digitalsuite.analysis.display.overview.page.title",
            name: "displayOverview",
            id: "accountreview_display_overview",
            state: "accountreview_website_display_overview",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            defaultQueryParams: {
                ...ignoreDisplayGroupParams,
            },
        },
        {
            title: "si.website-analysis.tab.publisher-overview",
            name: "publisherOverview",
            id: "accountreview_publisher_overview",
            state: "accountreview_website_publisher_overview",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            defaultQueryParams: {
                ...ignoreDisplayGroupParams,
            },
        },
        {
            title: "analysis.display.mediators.title",
            name: "adNetworks",
            id: "accountreview_display_mediators",
            state: "accountreview_website_display_ad_networks",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            ...navClaims(() => swSettings.components.WsWebDisplayTrafficAdNetworks),
            defaultQueryParams: {
                ...ignoreDisplayGroupParams,
            },
            itemComponentProps: {
                "data-automation-item": "analysis.display.mediators.title",
            },
        },
    ],
};

const socialGroup = {
    title: "Social",
    name: "social",
    hideIfNoSubItems: true,
    itemComponentProps: {
        id: "accountreview_social_group",
        text: "Social",
        isInitiallyOpened: () => accountReviewStorage.isWebsiteGroupOpened("accountreview_social_group"),
        "data-automation-group": "accountreview.social.title",
    },
    subItems: [
        {
            title: "analysis.social.overview.title",
            name: "overview",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            state: "accountreview_website_social_overview",
            ...navClaims(() => swSettings.components.WsWebSocialTrafficOverview),
        },
    ],
};

const contentGroup = {
    title: "analysis.content.title2",
    name: "websites",
    hideIfNoSubItems: true,
    itemComponentProps: {
        id: "accountreview_content",
        text: "Content",
        isInitiallyOpened: () => accountReviewStorage.isWebsiteGroupOpened("accountreview_content"),
        "data-automation-group": "analysis.content.title2",
    },
    subItems: [
        {
            title: "analysis.content.subdomains.title",
            name: "subdomains",
            options: {
                isVirtualSupported: false,
                isUSStatesSupported: true,
            },
            state: "accountreview_website_subdomains",
            defaultQueryParams: {
                state: "desktop",
            },
            ...navClaims(() => swSettings.components.WsWebContentSubdomains),
        },
        {
            title: "Pages",
            name: "pages",
            options: {
                isVirtualSupported: false,
                isUSStatesSupported: true,
            },
            state: "accountreview_website_website_structure_pages",
            defaultQueryParams: {
                state: "desktop",
            },
            ...navClaims(() => swSettings.components.PopularPagesV2),
        },
        {
            title: "Folders",
            name: "folders",
            options: {
                isVirtualSupported: false,
                isUSStatesSupported: true,
            },
            state: "accountreview_website_website_structure_folders",
            defaultQueryParams: {
                state: devicesTypes.TOTAL,
            },
            ...navClaims(() => swSettings.components.FoldersAnalysis),
        },
    ],
};

const getNavGroups = () => [
    companyGroup,
    getWebsiteGroup(),
    searchGroup,
    referralGroup,
    displayGroup,
    socialGroup,
    contentGroup,
];

const checkIfAllItemsAreLocked = (navList: INavItem[]) => {
    return navList.every((navItem) => {
        return (navItem.subItems as { lockIcon?: boolean; hidden?: boolean }[]).every((i) => i.lockIcon || i.hidden);
    });
};

function unlockAllSubItems(navList: INavItem[]): INavItem[] {
    return navList.map((navItem) => ({
        ...navItem,
        subItems: navItem.subItems?.map((subItem) => ({ ...subItem, lockIcon: false })) || [],
    }));
}

export const getSICompanyOverviewNavObj = (): {
    navList: INavItem[];
    areAllItemsLocked: boolean;
} => {
    const navList: INavItem[] = getNavGroups().map(applyNavItemPermissions);

    if (!checkIfAllItemsAreLocked(navList)) {
        return { navList, areAllItemsLocked: false };
    }

    return { navList: unlockAllSubItems(navList), areAllItemsLocked: true };
};
